import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { ColorSelection } from '../../../components/colorSelector/ColorSelector'
import { RatingType } from '../../../components/matchmaking/ChallengeCard'
import { ChallengeVariant, Challenges } from '../../../components/matchmaking/Challenges'
import { NewChallengeSelector } from '../../../components/newChallengeSelector/NewChallengeSelector'
import { LoggedInUserData } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import { ChallengeEntry, MMChallengeType, TimeMode } from '../../../store/types'
import { timeModeToString } from '../matchMakingConnector/MatchMakingConnectorModel'
import { MM_REQUESTS } from '../matchMakingConnector/MMTypes'

export type playAnyonePanelProps = {
    onCustomChallengeClick: () => void
    loggedIn: boolean
    userData: LoggedInUserData
}

function PlayAnyonePanel(props: playAnyonePanelProps) {
    const [isPlayAny, setPlayAny] = useState<boolean>(false)
    const { onCustomChallengeClick, loggedIn, userData } = props

    const sendPlayerChallenge = useStoreActions((state) => state.matchMaker.sendPlayerChallenge)
    const acceptChallenge = useStoreActions((state) => state.matchMaker.acceptChallenge)
    const sendDeleteChallenge = useStoreActions((state) => state.matchMaker.sendDeleteChallenge)
    const sendRejectChallenge = useStoreActions((state) => state.matchMaker.sendRejectChallenge)
    const trackRequest = useStoreActions((state) => state.matchMaker.trackRequest)
    const pendingRequests = useStoreState((state) => state.matchMaker.pendingRequests)

    // --------------------- Launched Challenges ---------------------

    const autoChallenges = useStoreState((state) => state.matchMaker.autoChallengesByMe)
    const customChallengesByMe = useStoreState((state) => state.matchMaker.customChallengesByMe)
    const directChallengesByMe = useStoreState((state) => state.matchMaker.directChallengesByMe)
    const challengesToMe = useStoreState((state) => state.matchMaker.challengesToMe)
    const openChallenges = useStoreState((state) => state.matchMaker.openChallenges)

    const autoChallengesStrings = useMemo(
        () =>
            Object.values(autoChallenges).map((challenge) => {
                return timeModeToString(challenge.timeMode)
            }),
        [autoChallenges],
    )

    const challengeButtons = [
        '1,0,bullet',
        '3,0,blitz',
        '3,2,blitz',
        '5,0,blitz',
        '5,3,blitz',
        '10,0,rapid',
        // '15,10,rapid',
        // '30,0,rapid',
    ]
    const pendingButtons = challengeButtons.filter((cstr) => pendingRequests[cstr])
    const pendingAcceptId = pendingRequests[MM_REQUESTS.CHALLENGE_ACCEPT]?.id

    useEffect(() => {
        if (challengeButtons.every((challenge) => autoChallengesStrings.includes(challenge))) {
            setPlayAny(true)
        } else {
            setPlayAny(false)
        }
    }, [autoChallengesStrings])

    const onSelectedTimeHandler = (newSelectedAutoChallenges: string[]) => {
        if (!loggedIn) return

        const challengeMap: Record<string, ChallengeEntry> = {}
        Object.values(autoChallenges).map((c) => (challengeMap[timeModeToString(c.timeMode)] = c))

        challengeButtons.forEach((cstr) => {
            if (autoChallengesStrings.includes(cstr) && !newSelectedAutoChallenges.includes(cstr)) {
                const challengeId = challengeMap[cstr].challengeId
                if (!trackRequest({ key: cstr, requestType: MM_REQUESTS.CHALLENGE_DELETE, id: challengeId })) return
                sendDeleteChallenge({
                    challengeId: challengeMap[cstr].challengeId,
                    challengeType: challengeMap[cstr].challengeType,
                })
                let challengeString = timeModeToString(challengeMap[cstr].timeMode)
                challengeString = challengeString.replace(/,/g, '-')
                analyticsManager.dispatchEvent('exitAutoPool', {
                    minutes: challengeMap[cstr].timeMode.durationMinutes,
                    increment: challengeMap[cstr].timeMode.clockIncrementSeconds,
                    challengeID: challengeMap[cstr].challengeId,
                    gameType: challengeMap[cstr].timeMode.name,
                    challengeString: challengeString, // GTM expects this parameter
                    challengeType: 'auto_pool', // GTM expects this parameter
                })
            }
            if (!autoChallengesStrings.includes(cstr) && newSelectedAutoChallenges.includes(cstr)) {
                // pending tiles are part of the group value too; trackRequest turns them away
                if (!trackRequest({ key: cstr, requestType: MM_REQUESTS.CHALLENGE_CREATE, id: cstr })) return
                const parts = cstr.split(',')
                const challenge = {
                    color: ColorSelection.RANDOM,
                    rated: true,
                    time: Number(parts[0]),
                    increment: Number(parts[1]),
                    challengeType: MMChallengeType.AUTO,
                }
                sendPlayerChallenge(challenge)
                let challengeString = cstr.replace(/,/g, '-')
                analyticsManager.dispatchEvent('enterAutoPool', {
                    minutes: challenge.time,
                    increment: challenge.increment,
                    challengeID: challengeString,
                    gameType: challengeString.split('-')[2] as RatingType,
                    challengeString: challengeString, // GTM expects this parameter
                    challengeType: 'auto_pool', // GTM expects this parameter
                })
            }
        })
    }

    // const handlePlayAny = () => {
    //     if (isPlayAny) {
    //         onSelectedTimeHandler([])
    //     } else {
    //         onSelectedTimeHandler([])
    //         shuffleArray(challengeButtons).forEach((challenge) => onSelectedTimeHandler([challenge]))
    //     }
    // }

    const onDeleteChallengeHandler = (challenge: ChallengeEntry) => {
        if (challenge.challengeType === MMChallengeType.DIRECT && challenge.pairId) {
            sendRejectChallenge({ pairId: challenge.pairId, isRematch: false })
        } else {
            sendDeleteChallenge({ challengeId: challenge.challengeId, challengeType: challenge.challengeType })
        }
    }

    const onAcceptChallengeHandler = (challenge: ChallengeEntry) => {
        if (!!challenge.challengeId) {
            // one accept in flight at a time: MM queues a connection's requests, so re-taps only add load
            const key = MM_REQUESTS.CHALLENGE_ACCEPT
            if (!trackRequest({ key, requestType: key, id: challenge.challengeId })) return
            acceptChallenge({
                challengeType: challenge.challengeType,
                id: challenge.challengeType === MMChallengeType.DIRECT ? challenge.pairId! : challenge.challengeId,
            })
        }
    }

    function calculateEtime(timeMode: TimeMode) {
        return timeMode.durationMinutes + (timeMode.clockIncrementSeconds * 2) / 3
    }

    function sortChallenges(array: ChallengeEntry[]) {
        if (array && array.length > 0) {
            array.sort((a, b) => {
                const etimeA = calculateEtime(a.timeMode)
                const etimeB = calculateEtime(b.timeMode)
                if (etimeA !== etimeB) {
                    return etimeA - etimeB
                }
                const createdA = a.createdTime || 0
                const createdB = b.createdTime || 0
                return createdB - createdA
            })
        }
    }

    const sortedOpenChallenges = Object.values(openChallenges)
    const sortedChallengesToMe = Object.values(challengesToMe)
    const sortedDirectChallengesByMe = Object.values(directChallengesByMe)
    const sortedCustomChallengesByMe = Object.values(customChallengesByMe)

    sortChallenges(sortedOpenChallenges)
    sortChallenges(sortedChallengesToMe)
    sortChallenges(sortedDirectChallengesByMe)
    sortChallenges(sortedCustomChallengesByMe)

    return (
        <Box p={0} pb="16px" gap="16px" display="flex" flexDirection="column">
            <NewChallengeSelector
                headerTitle="Click to be auto paired"
                launchedChallenges={[...new Set([...autoChallengesStrings, ...pendingButtons])]}
                pendingChallenges={pendingButtons}
                onSelected={onSelectedTimeHandler}
                vsHuman={true}
                buttons={challengeButtons}
                isPlayAny={isPlayAny}
                handleCustomButton={onCustomChallengeClick}
                // handlePlayAny={handlePlayAny}
            />

            {loggedIn && (
                <>
                    <Challenges
                        variant={ChallengeVariant.DIRECT_CHALLENGES}
                        challenges={sortedChallengesToMe}
                        handleAction={onAcceptChallengeHandler}
                        pendingChallengeId={pendingAcceptId}
                    />
                    <Challenges
                        variant={ChallengeVariant.MY_CHALLENGES}
                        challenges={sortedCustomChallengesByMe.concat(sortedDirectChallengesByMe)}
                        handleAction={onDeleteChallengeHandler}
                    />
                    <Challenges
                        variant={ChallengeVariant.OPEN_CHALLENGES}
                        challenges={sortedOpenChallenges}
                        handleAction={onAcceptChallengeHandler}
                        pendingChallengeId={pendingAcceptId}
                    />
                </>
            )}
        </Box>
    )
}

export default PlayAnyonePanel
